import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from "reactstrap";
import "../css/TableSelect.css";
import TableService from "../../../../../services/OrderingServices/TableService";
import SectionService from "../../../../../services/OrderingServices/SectionService";
import Select from "react-select";

import PropTypes from "prop-types";
import { setOrderId } from "../../../../../slices/Order/orderSlice";
import toast from "react-hot-toast";

const TableSwitch = ({
  isOpen,
  toggle = () => {},
  selectedTable,
  setSelectedTable = () => {},
  data,
  sectionData = [],
  setSelectedSection = () => {},
  selectedSection = null,
  loading,
  mode = "switch-table", // "switch-table" or "switch-item"
  selectedItems,
  orderId,
  fetchOrder,
  orderData,
  fetchTable,
}) => {
  console.log("🚀 ~ TableSwitch ~ selectedItems:", selectedItems);

  // 🔁 Hooks always first
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelectedTable, setTempSelectedTable] = useState(null);
  console.log("🚀 ~ TableSwitch ~ tempSelectedTable:", tempSelectedTable);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // ❌ Early return AFTER all hooks
  if (!isOpen) return null;

  const handleSectionChange = (selectedOption) => {
    setSelectedSection(selectedOption);
    setTempSelectedTable(null);
  };

  const handleSectionCardClick = (section) => {
    handleSectionChange({ value: section.id, label: section.name });
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return <span className="status-check">✓</span>;
      case "occupied":
        return <span className="status-cross">✗</span>;
      case "reserved":
        return <span className="status-reserved">R</span>;
      default:
        return null;
    }
  };

  const filteredTables =
  data?.tables?.filter((table) => {
    const matchesSearch = table.tableName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const status = table.tableStatus?.toLowerCase();

    // For table switching, only show available tables
    // For item switching, only show occupied tables
    const matchesFilter = (() => {
      if (mode === "switch-table") {
        if (filterStatus === "all") {
          return status === "available"; // Only available tables for switching
        }
        return status === "available" && status === filterStatus;
      } else {
        // For item switching, only show occupied tables
        return status === "occupied";
      }
    })();

    const matchesSection =
      !selectedSection ||
      table.section?.id === selectedSection?.value ||
      (!table.section && data?.id === selectedSection?.value);
    return matchesSearch && matchesFilter && matchesSection;
  }) || [];

  const handleConfirm = async () => {
    try {
      let response;

      if (mode === "switch-table") {
        response = await TableService.switchTable(
          orderId,
          tempSelectedTable?.id
        );
      } else {
        const itemIndexes = selectedItems.map((key) => {
          const [orderIndex, itemIndex] = key.split("-").map(Number);
          return { orderIndex, itemIndex };
        });
        response = await TableService.switchItem(
          tempSelectedTable?.id,
          itemIndexes.map((i) => orderData[i.orderIndex].items[i.itemIndex].id)
        );
      }

      fetchOrder();
      fetchTable();
      toast.success(response);

      if (tempSelectedTable) {
        setSelectedTable(tempSelectedTable);
        toggle();
      }

      console.log("Switch action response:", response);
    } catch (error) {
      console.error("Error in handleConfirm:", error);
      toast.error(error?.message || "Something went wrong");
    }
  };

  const sectionOptions = sectionData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  // Get filter options based on mode
  const getFilterOptions = () => {
    if (mode === "switch-table") {
      return ["all"]; // Only show "all" for table switching (which means available only)
    }
    return ["all",  "occupied", ];
  };

  const renderTableVisual = (table, scale = 0.6) => {
    return (
      <div
        className={`table-card table-${table.tableStatus?.toLowerCase()} table-capacity-${
          table.capacity
        }`}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          pointerEvents: "none",
        }}
      >
        <div
          className={`chair-group chair-top ${
            table.capacity >= 8 ? "chair-wide" : ""
          }`}
        >
          {[
            ...Array(
              table.capacity === 2
                ? 1
                : table.capacity >= 12
                ? 3
                : table.capacity >= 8
                ? 2
                : 1
            ),
          ].map((_, i) => (
            <div key={`top-${i}`} className="chair" />
          ))}
        </div>
        <div
          className={`chair-group chair-bottom ${
            table.capacity >= 8 ? "chair-wide" : ""
          }`}
        >
          {[
            ...Array(
              table.capacity === 2
                ? 1
                : table.capacity >= 12
                ? 3
                : table.capacity >= 8
                ? 2
                : 1
            ),
          ].map((_, i) => (
            <div key={`bottom-${i}`} className="chair" />
          ))}
        </div>
        {table.capacity > 2 && (
          <div className="chair-group chair-left">
            {[...Array(table.capacity >= 12 ? 2 : 1)].map((_, i) => (
              <div key={`left-${i}`} className="chair" />
            ))}
          </div>
        )}
        {table.capacity > 2 && (
          <div className="chair-group chair-right">
            {[...Array(table.capacity >= 12 ? 2 : 1)].map((_, i) => (
              <div key={`right-${i}`} className="chair" />
            ))}
          </div>
        )}
        <div className="table-content">
          <div className="table-name">{table.tableName}</div>
          <div className="table-status-info">
            <div className="status-icon-wrapper">
              {getStatusIcon(table.tableStatus)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSelectedTableInfo = () => {
    if (!selectedTable && !tempSelectedTable) return null;

    return (
      <Card className="selected-table-info mb-3  border bg-light">
        <CardBody className="rounded-3">
          {/* Current Table */}
          {selectedTable && (
            <div className="d-flex align-items-center justify-content-center align-content-center gap-3 mb-3">
              <div className="selected-table-visual">
                {renderTableVisual(selectedTable, 0.7)}
              </div>

              <div className="selected-table-details flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1 fw-bold">
                      Current Table: {selectedTable.tableName}
                    </h6>
                    <div className="d-flex align-items-center gap-2">
                      {/* <span
                      className={`badge badge-${selectedTable.tableStatus?.toLowerCase()}`}
                    >
                      {selectedTable.tableStatus}
                    </span> */}
                      <span className="text-muted small">
                        <i className="ri-user-line me-1"></i>
                        {selectedTable.capacity} seats
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Switch Arrow and Selected Table to Switch To */}
          {tempSelectedTable && (
            <>
              <div className="text-center mb-3">
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <hr className="flex-grow-1" />
                  <div className="switch-arrow-container">
                    <i className="ri-arrow-down-line text-primary fs-5"></i>
                    <span className="text-muted small d-block">
                      {mode === "switch-table" ? "Switch to" : "Move items to"}
                    </span>
                  </div>
                  <hr className="flex-grow-1" />
                </div>
              </div>

              <div className="d-flex align-items-center gap-3">
                <div className="selected-table-visual">
                  {renderTableVisual(tempSelectedTable, 0.7)}
                </div>

                <div className="selected-table-details flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1 fw-bold text-success">
                        {mode === "switch-table"
                          ? "New Table:"
                          : "Target Table:"}{" "}
                        {tempSelectedTable.tableName}
                      </h6>
                      <div className="d-flex align-items-center gap-2">
                        {/* <span
                        className={`badge badge-${tempSelectedTable.tableStatus?.toLowerCase()}`}
                      >
                        {getStatusIcon(tempSelectedTable.tableStatus)}
                        {tempSelectedTable.tableStatus}
                      </span> */}
                        <span className="text-muted small">
                          <i className="ri-user-line me-1"></i>
                          {tempSelectedTable.capacity} seats
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Show instruction when no table is selected to switch to */}
          {selectedTable && !tempSelectedTable && (
            <div className="text-center py-3 border-top">
              <span className="text-muted small">
                <i className="ri-hand-finger-line me-1"></i>
                Select a table below to{" "}
                {mode === "switch-table" ? "switch to" : "move items to"}
              </span>
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      backdropClassName="bg-dark bg-opacity-50"
      className="shadow-lg animate__animated animate__slideInRight bg-white rounded-start"
      id="offcanvasRight"
    >
      {/* OffcanvasHeader for mobile */}
      <OffcanvasHeader className="d-block d-sm-none">
        <div className="d-flex justify-content-between align-items-center w-100 rounded-top">
          <button className="btn btn-sm btn-light" onClick={toggle}>
            <i className="bx bx-chevron-left"></i>
          </button>
          <div className="d-flex align-items-center text-center justify-content-center w-100 gap-2">
            <i className="ri-stack-line"></i>
            <h5 className="mb-0">
              {mode === "switch-table" ? "Table Switch" : "Move Items"}
            </h5>
          </div>
        </div>
      </OffcanvasHeader>

      <div
        className="position-absolute d-none d-sm-block"
        style={{ left: "-40px", top: "10px" }}
      >
        <button className="btn btn-sm btn-light" onClick={toggle}>
          <i className="ri-close-line"></i>
        </button>
      </div>

      {/* Desktop Header */}
      <div className="d-none d-sm-flex justify-content-start align-items-center p-3 shadow-sm rounded-top">
        <i className="ri-stack-line"></i>
        <h5 className="mb-0 ms-2">
          {mode === "switch-table" ? "Table Switch" : "Move Items"}
        </h5>
      </div>

      <OffcanvasBody className="px-lg-4 px-2 py-3 bg-body-tertiary">
        {/* Selected Table Info */}

        {/* Filter Controls */}
        <div>
          <Row className="py-2 px-3">
            <Col lg={3} xs={10} className="mb-2">
              <Select
                options={sectionOptions}
                value={selectedSection}
                onChange={handleSectionChange}
                placeholder="Select section"
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base, state) => ({
                    ...base,
                    fontSize: "12px",
                    padding: "1px",
                    borderRadius: "20px",
                    borderColor: state.isFocused ? "#2684FF" : base.borderColor,
                    boxShadow: state.isFocused
                      ? "0 0 0 1px #2684FF"
                      : base.boxShadow,
                    "&:hover": {
                      borderColor: "#2684FF",
                    },
                  }),
                }}
              />
            </Col>

            <Col md={12} xs={12} lg={4} className="mb-2 d-lg-block d-none ">
              <div className="position-relative">
                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Input
                  type="text"
                  placeholder="Search for table..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-5 py-2 rounded-pill border shadow-sm w-100"
                  disabled={!selectedSection}
                  style={{ fontSize: "14px" }}
                />
                {searchQuery && (
                  <i
                    className="ri-close-line position-absolute top-50 end-0 translate-middle-y me-3 text-danger cursor-pointer"
                    onClick={() => setSearchQuery("")}
                  />
                )}
              </div>
            </Col>

            {/* Filters */}
            <Col md={12} lg={5} className="d-lg-block d-none">
              <div className="filter-pills" disabled={!selectedSection}>
                {getFilterOptions().map((status) => (
                  <button
                    key={status}
                    className={`filter-pill ${status}-pill ${
                      filterStatus === status ? "active" : ""
                    }`}
                    onClick={() => setFilterStatus(status)}
                    disabled={!selectedSection}
                  >
                    {status === "available" && (
                      <span className="pill-dot available-dot"></span>
                    )}
                    {status === "occupied" && (
                      <span className="pill-dot occupied-dot"></span>
                    )}
                    {status === "reserved" && (
                      <span className="pill-dot reserved-dot"></span>
                    )}
                    {mode === "switch-table" && status === "all"
                      ? "Available Tables"
                      : status
                          .replace("-", " ")
                          .replace(/^\w/, (c) => c.toUpperCase())}
                  </button>
                ))}
              </div>
            </Col>

            {/* Mobile Filters Toggle */}
            <Col xs={2} className="d-block d-lg-none ">
              <div className="d-flex align-items-center align-content-center py-1">
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => setShowMobileFilters(true)}
                  disabled={!selectedSection}
                >
                  <i className="ri-filter-line"></i>
                </button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Mobile Filter Offcanvas */}
        <Offcanvas
          isOpen={showMobileFilters}
          toggle={() => setShowMobileFilters(false)}
          direction="bottom"
          className="d-block d-lg-none"
          style={{
            height: "230px",
            transition: "transform 0.3s ease",
            borderTopLeftRadius: "1rem",
            borderTopRightRadius: "1rem",
            overflow: "hidden",
          }}
        >
          <OffcanvasHeader toggle={() => setShowMobileFilters(false)}>
            Filters
          </OffcanvasHeader>
          <div className="p-2">
            <div className="mb-4 ">
              <div className="position-relative">
                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Input
                  type="text"
                  placeholder="Search for table..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-5 py-2 rounded-pill border shadow-sm w-100"
                  style={{ fontSize: "14px" }}
                />
                {searchQuery && (
                  <i
                    className="ri-close-line position-absolute top-50 end-0 translate-middle-y me-3 text-danger cursor-pointer"
                    onClick={() => setSearchQuery("")}
                  />
                )}
              </div>
            </div>
            <div className="filter-pills">
              {getFilterOptions().map((status) => (
                <button
                  key={status}
                  className={`filter-pill ${status}-pill ${
                    filterStatus === status ? "active" : ""
                  }`}
                  onClick={() => setFilterStatus(status)}
                >
                  {status === "available" && (
                    <span className="pill-dot available-dot"></span>
                  )}
                  {status === "occupied" && (
                    <span className="pill-dot occupied-dot"></span>
                  )}
                  {status === "reserved" && (
                    <span className="pill-dot reserved-dot"></span>
                  )}
                  {mode === "switch-table" && status === "all"
                    ? "Available Tables"
                    : status
                        .replace("-", " ")
                        .replace(/^\w/, (c) => c.toUpperCase())}
                </button>
              ))}
            </div>
          </div>
        </Offcanvas>

        {/* Tables */}
        <div className="table-grid-wrapper">
          <Row>
            <Col lg={4}>{renderSelectedTableInfo()}</Col>
            <Col lg={8}>
              <Card className="">
                <CardBody className="bg-light rounded-3 ">
                  <div className="table-grid">
                    {loading ? (
                      <div className="d-flex justify-content-center align-items-center w-100 my-5">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        />
                      </div>
                    ) : !selectedSection ? (
                      <Row className="w-100">
                        {sectionData.map((item) => (
                          <Col key={item.id} md={3} lg={3} xs={6}>
                            <Card
                              onClick={() => handleSectionCardClick(item)}
                              className="shadow-sm border-0 rounded-4 cursor-pointer hover-scale"
                              style={{
                                transition: "transform 0.3s ease-in-out",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.transform =
                                  "scale(1.05)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.transform = "scale(1)")
                              }
                            >
                              <CardBody className="text-center d-flex flex-column align-items-center justify-content-center">
                                <div
                                  className="bg-light text-primary rounded-circle d-flex align-items-center justify-content-center mb-3"
                                  style={{ width: 50, height: 50 }}
                                >
                                  <i className="ri-folder-3-line ri-xl"></i>
                                </div>
                                <h6 className="fw-bold text-dark mb-0">
                                  {item.name}
                                </h6>
                              </CardBody>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    ) : filteredTables.length > 0 ? (
                      filteredTables.map((table) => {
                        const isCurrentTable = selectedTable?.id === table.id;
                        const isSelectable =
                          mode === "switch-item" ||
                          (mode === "switch-table" &&
                            table.tableStatus?.toLowerCase() === "available" &&
                            !isCurrentTable);

                        return (
                          <div
                            key={table.id}
                            onClick={() => {
                              if (isSelectable) {
                                setTempSelectedTable(
                                  tempSelectedTable?.id === table.id
                                    ? null
                                    : table
                                );
                              }
                            }}
                            className={`table-card table-${table.tableStatus?.toLowerCase()} table-capacity-${
                              table.capacity
                            } ${
                              tempSelectedTable?.id === table.id
                                ? "table-selected"
                                : ""
                            } ${isCurrentTable ? "table-current" : ""} ${
                              !isSelectable ? "table-disabled" : ""
                            }`}
                          >
                            {/* Current table indicator */}
                            {isCurrentTable && (
                              <div className="table-current-indicator">
                                <i className="ri-check-line"></i>
                              </div>
                            )}

                            <div
                              className={`chair-group chair-top ${
                                table.capacity >= 8 ? "chair-wide" : ""
                              }`}
                            >
                              {[
                                ...Array(
                                  table.capacity === 2
                                    ? 1
                                    : table.capacity >= 12
                                    ? 3
                                    : table.capacity >= 8
                                    ? 2
                                    : 1
                                ),
                              ].map((_, i) => (
                                <div key={`top-${i}`} className="chair" />
                              ))}
                            </div>
                            <div
                              className={`chair-group chair-bottom ${
                                table.capacity >= 8 ? "chair-wide" : ""
                              }`}
                            >
                              {[
                                ...Array(
                                  table.capacity === 2
                                    ? 1
                                    : table.capacity >= 12
                                    ? 3
                                    : table.capacity >= 8
                                    ? 2
                                    : 1
                                ),
                              ].map((_, i) => (
                                <div key={`bottom-${i}`} className="chair" />
                              ))}
                            </div>
                            {table.capacity > 2 && (
                              <div className="chair-group chair-left">
                                {[...Array(table.capacity >= 12 ? 2 : 1)].map(
                                  (_, i) => (
                                    <div key={`left-${i}`} className="chair" />
                                  )
                                )}
                              </div>
                            )}
                            {table.capacity > 2 && (
                              <div className="chair-group chair-right">
                                {[...Array(table.capacity >= 12 ? 2 : 1)].map(
                                  (_, i) => (
                                    <div key={`right-${i}`} className="chair" />
                                  )
                                )}
                              </div>
                            )}
                            <div className="table-content">
                              <div className="table-name">
                                {table.tableName}
                              </div>
                              <div className="table-status-info">
                                <div className="status-icon-wrapper">
                                  {getStatusIcon(table.tableStatus)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center my-5">
                        <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                        <h5 className="fw-semibold text-secondary">
                          {mode === "switch-table"
                            ? "No Available Tables"
                            : "No Tables Found"}
                        </h5>
                        <p className="text-muted mb-0">
                          {mode === "switch-table"
                            ? "All tables are currently occupied. Please check back later."
                            : "Please adjust your filters or check back later."}
                        </p>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </OffcanvasBody>

      <ModalFooter className="p-2" style={{ backgroundColor: "#f9fafb" }}>
        <div className="footer-content d-flex justify-content-between w-100">
          {tempSelectedTable && (
            <span className="d-lg-flex d-none w-100">
              {mode === "switch-table" ? "Switch to" : "Move items to"}:{" "}
              <strong>{tempSelectedTable.tableName}</strong>
            </span>
          )}
          <div className="text-end w-100">
            <button
              onClick={handleConfirm}
              disabled={!tempSelectedTable}
              className="btn btn-success"
            >
              {mode === "switch-table" ? "Switch Table" : "Move Items"}
            </button>
          </div>
        </div>
      </ModalFooter>
    </Offcanvas>
  );
};

TableSwitch.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  selectedTable: PropTypes.object,
  setSelectedTable: PropTypes.func,
  data: PropTypes.shape({
    id: PropTypes.number,
    tables: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        tableName: PropTypes.string,
        capacity: PropTypes.number,
        tableStatus: PropTypes.string,
        section: PropTypes.object,
      })
    ),
  }),
  sectionData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  setSelectedSection: PropTypes.func,
  selectedSection: PropTypes.object,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(["switch-table", "switch-item"]),
};

export default TableSwitch;
